import { ChangeDetectorRef, Component, HostListener, OnInit, Optional } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NavigationItem } from '@app/shared/types/navigation.types'
import { NavigationService } from '../navigation.service'
import { Organization } from '@app/model/organization.model'
import { I18nService } from '@app/core/i18n.service'
import { AuthService } from '@app/core/auth/auth.service'
import { catchError, distinctUntilChanged, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { FeatureFlagService } from '@app/shared/directives/feature-flag/feature-flag.service'
import { environment } from '@env/environment'
import { AccessDeniedError, ForbiddenOrganizationError, MyAccountService, OrganizationNotFoundError } from '@app/core/my-account.service';
import { HttpErrorResponse } from '@angular/common/http'
import { ORGANIZATIONS_ROUTE, REPORTS_ROUTE } from '@app/constant/navigation'
import { MainInfo } from './main.info-resolver'

@Component({
    selector: 'main-layout',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    standalone: false
})
export class MainLayoutComponent implements OnInit {

    fullYear: number = (new Date()).getFullYear();
    baseLang: string = '';
    isSmallScreen: boolean = false;

    /**
     * List of navigation items to show in the nav component(s)
     */
    navigationItems!: (NavigationItem & { hidden?: boolean; })[];

    currentNavigationIndex!: number | undefined;

    organization$!: Observable<Organization | undefined>;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _navigationService: NavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        protected i18nService: I18nService,
        @Optional() private _authService: AuthService | null,
        private _featureFlagService: FeatureFlagService,
        private _myAccountService: MyAccountService,
    ) {
        // Pipe necessary route data
        const mainInfo = this._activatedRoute.snapshot.data['info'] as MainInfo;

        // Assign current selected locale
        const localId = this.i18nService.selectedLocaleId.split('-');
        this.baseLang = localId[0] + '-' + localId[1].toUpperCase();

        this.navigationItems = this._navigationService.navigationItems.map(item => {
            switch (item.id) {
                case ORGANIZATIONS_ROUTE.id:
                    return { ...item, hidden: mainInfo.organizationsCount === 1 };
                case REPORTS_ROUTE.id:
                    return { ...item, hidden: !this._featureFlagService.getFeature('reports') };
            
                default:
                    return { ...item, hidden: false };
            }
        });
        this.currentNavigationIndex = this._navigationService.currentNavigationIndex;
        this.organization$ = this._myAccountService.currentOrganizationDirectory$.pipe(
            switchMap(code => {
                if (code === undefined) {
                    console.log("You don't have a selected organization to show in the navigation bar");
                    return of(undefined);
                } else {
                    return this._myAccountService.selectedAuthorizedOrganization$.pipe(
                        tap(organization => (console.log("Displaying your selected organization in the navigation bar:", organization.organizationCode))),
                        catchError((err: Error) => {
                            if (err instanceof ForbiddenOrganizationError) {
                                console.log('Redirecting to select organization page as you dont have access to the organization', code.toUpperCase());
                                this._myAccountService.clearOrganizationDirectorySelection(); // Already called inside home component
                                this._router.navigate(['organizations']);
                            }
                            if (err instanceof OrganizationNotFoundError) {
                                console.log("Cannot find the organization with code:", code.toUpperCase())
                                console.log("Redirecting to select organization page");
                                this._myAccountService.clearOrganizationDirectorySelection(); // Already called inside home component
                                this._router.navigate(['organizations']);
                            }
                            return throwError(() => err);
                        }),
                    );
                }
            }),
            map(org => org ? { ...org } : undefined), // Force new object reference
            shareReplay(1), // Cache latest value + replay
            // TODO - add catch error if needed
        );
    }

    ngOnInit(): void {
        this._navigationService.currentNavigationIndex$.pipe(distinctUntilChanged()).subscribe(index => {
            this.currentNavigationIndex = index;
            this._changeDetectorRef.markForCheck();
        });
        this._myAccountService.getOrganizationsCount().subscribe({
            next: count => {
                this.navigationItems = this._navigationService.navigationItems.map(item => {
                    switch (item.id) {
                        case ORGANIZATIONS_ROUTE.id:
                            return { ...item, hidden: count === 1 };
                        case REPORTS_ROUTE.id:
                            return { ...item, hidden: !this._featureFlagService.getFeature('reports') };
                    
                        default:
                            return { ...item, hidden: false };
                    }
                });
                this._changeDetectorRef.markForCheck();
            },
            error: (err: Error) => {
                console.error('Pulling authorized organizations count failed:', err.message);
                if (err instanceof AccessDeniedError) {
                    console.log('Redirecting to access denied page');
                    this._router.navigate(['access-denied']);
                    return;
                }
                throw err;
            },
        });
        
        if (environment.b2c !== undefined) {
            if (this._myAccountService.isAuthenticated) {
                this._myAccountService.startMsalSessionExpirationMonitoring();
            }
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.isSmallScreen = window.innerWidth < 680;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * 
     * @param route 
     */
    navClick(nav: NavigationItem, orgCode: string, navIndex: number) {
        this.currentNavigationIndex = navIndex;
        this._navigationService.currentNavigationId = nav.id;
        if (nav.id === ORGANIZATIONS_ROUTE.id) {
            this._myAccountService.clearOrganizationDirectorySelection();
        }
        this._router.navigate([nav.link.replace(':organizationCode', orgCode)]);
    }

    /**
     * 
     */
    logOut(): void {
        if (environment.b2c === undefined) {
            this._authService!.logOutWithWarningPopup();
        } else {
            this._myAccountService.logoutRedirect();
        }
    }

    getFaqFile(type: "GWS" | "GBA") {
        const selectedLocaleId = this.i18nService.selectedLocaleId.toLocaleLowerCase();
        switch (type) {
            case ('GWS'):
                return environment.supportedLanguages[selectedLocaleId].handbooks.gws;
            case ('GBA'):
                return environment.supportedLanguages[selectedLocaleId].handbooks.gba;
        }
    }

    openFaqFile(type: "GWS" | "GBA") {
        const faqFile = this.getFaqFile(type);
        if (faqFile !== undefined) window.open(faqFile, '_blank');
    }

    /**
     * This method uses the Angular Router to redirect the user to the `sample-results` route.
     * Optionally, query parameters or state can be passed to the route if required.
     *
     */
    openSampleReport(instrumentType: string): void {
        this._router.navigate(['sample-reports', instrumentType]);
    }

    /**
     * This methos is added to open privacy notice in new window
     */
    openPrivacyPolicy() {
        const selectedLocaleId = this.i18nService.selectedLocaleId.toLocaleLowerCase();
        window.open(environment.supportedLanguages[selectedLocaleId].privacyPolicy, '_blank')
    }

    openTermsOfUse(){
        const selectedLocaleId = this.i18nService.selectedLocaleId.toLocaleLowerCase();
        window.open(environment.supportedLanguages[selectedLocaleId].termsOfUse, '_blank')
    }
}
